//Rules that can be set to different form fields to check if the user has inputted valid values

import { isValid, parse, startOfDay, isBefore, isAfter, isEqual } from 'date-fns'
import DateUtils from "@/modules/core/services/date-utils";

/**
 * Checks if the value is not null.
 *
 * @param text to return if value is not valid
 * @returns {function(*=): boolean|*}
 */
export function ruleRequired(text) {
  return v => (v === 0 || !!v) || text;
}

/**
 * Checks if at least one is selected
 *
 * @param text to return if value is not valid
 * @returns {function(*=): boolean|*}
 */
export function ruleRequiredMultiple(text) {
  return v => !!v.length || text;
}

/**
 * Checks if the user has selected all options
 *
 * @param text to return if user selected everything
 * @param count length of the list to compare
 * @returns {function(*)}
 */
export function ruleNotAllSelected(text, count) {
  return v => !count || count != 0 && v.length < count || text;
}

/**
 * Checks if the length of the value is equals/larger the given length.
 *
 * @param length minimum length to check
 * @param text to return if value is not valid
 * @returns {function(*): boolean|*}
 */
export function ruleMinLength(length, text) {
  return v => v != null && v.length >= length || text;
}

/**
 * Checks if the value empty or length of the value is between the given length range.
 *
 * @param from
 * @param to
 * @param text
 * @returns {(function(*): (boolean|*))|*}
 */
export function ruleLengthBeetweenOrEmpty(from, to, text) {
  return (v) => {
    if (v === null || v === "" || v === undefined) return true;
    if (v.length >= from && v.length <= to) return true;

    return  text;
  }
}

/**
 * Checks if the value is either null or if a text is inserted, must have a min length.
 *
 * @param length minimum length to check
 * @param text to return if value is not valid
 * @returns {function(*=): boolean|*}
 */
export function ruleEmptyOrMinLength(length, text) {
  return v => !v || v.length == 0 || v.length >= length || text
}

/**
 * Checks if the value is a valid latitude value.
 *
 * @param text to return if value is not valid
 * @returns {function(*): boolean|*}
 */
export function ruleLatitude(text) {
  return (v) => (v > -90 && v < 90) || text;
}

/**
 * Checks if the value is a valid longitude value.
 *
 * @param text to return if value is not valid
 * @returns {function(*): boolean|*}
 */
export function ruleLongitude(text) {
  return (v) => (v > -180 && v < 180) || text;
}

/**
 * Checks if the value is a valid latitude value.
 *
 * @param text to return if value is not valid
 * @returns {function(*): boolean|*}
 */
export function ruleLatitudeOrEmpty(text) {
  return (v) => {
    if (v === null || v === "" || v === undefined) return true;
    if (v > -90 && v < 90) return true;

    return text;
  }
}

/**
 * Checks if the value is a valid longitude value.
 *
 * @param text to return if value is not valid
 * @returns {function(*): boolean|*}
 */
export function ruleLongitudeOrEmpty(text) {
  return (v) => {
    if (v === null || v === "" || v === undefined) return true;
    if (v > -180 && v < 180) return true;

    return text;
  }
}

/**
 * Checks if the value is less than 32
 *
 * @param text to return if value is not valid
 */
export function ruleDaysOfMonth(text) {
  return (v) => (v < 32) || text;
}

/**
 * Checks if the value is bigger or equal 0
 *
 * @param text to return if value is not valid
 */
export function ruleNotNegative(text) {
  return (v) => (v >= 0) || text;
}


/**
 * Checks if the value is bigger or equal 0 and less or equal 100
 *
 * @param text to return if value is not valid
 */
export function ruleHundredPercent(text) {
  return (v) => (v >= 0 && v <= 100) || text;
}
/**
 * Checks if the value is bigger than 0
 *
 * @param text to return if value is not valid
 */
 export function ruleGreaterZero(text) {
  return (v) => (v > 0) || text;
}

/**
 * Checks if the value is larger than a specific size (in KB).
 *
 * @param size limit in KB
 * @param text to display on failure
 * @returns {function(*=): *}
 */
export function ruleMaxFileSize(size, text) {
  return (file) => !file || file.size / 1024 < size || text
}

/**
 * Checks if the given text is a numerical number
 * @param {*} text
 * @returns
 */
export function ruleIsInteger(text) {
  return v =>  (/^[0-9]+$/.test(v)) || text;
}

/**
 * Checks if the given text is either integer or empty
 * @param {*} text
 * @returns
 */
export function ruleIsEmptyOrInteger(text) {
  return v =>  (/^[0-9]+$/.test(v) || v === null || v === "" || v === undefined) || text;
}

/**
 * Checks if the given text is a number (decimal possible)
 * @param {*} text
 * @returns
 */
export function ruleIsNumber(text) {
  return v =>  (!isNaN(v)) || text;
}

/**
 * Checks if the given text is a number (decimal possible) or empty
 * @param {*} text
 * @returns
 */
export function ruleIsEmptyOrNumberText(text) {
  return v =>  (v === null || v === "" || v === undefined || !isNaN(v)) || text;
}

/**
 * Checks if the given text is a option counter value (two integers separated by a ':', e.g. 5;5, 1;10, ...)
 * @param {*} text
 * @returns
 */
export function ruleIsOptionCounter(text) {
  return v =>  (/^[0-9]+;[0-9]+$/.test(v)) || text;
}

/**
 * Checks if the given text is in between from and to
 * @param from value to start
 * @param to value to end
 * @param text rule text
 * @returns {function(*)}
 */
export function ruleNumberFromTo(from, to, text) {
  return (v) => {
    if (v === null || v === "" || v === undefined) return true;
    if (v >= from && v <= to) return true;

    return text;
  }
}

/**
 * Checks if the given text is equal or bigger
 * @param from value to start
 * @param text rule text
 * @returns {function(*)}
 */
export function ruleNumberFrom(from, text) {
  return (v) => {
    if (v === null || v === "" || v === undefined) return true;
    if (v >= from) return true;

    return text;
  }
}

/**
 * checks if the entered date is a valid date
 *
 * @param text text if date is not valid
 * @returns {(function(*=): (boolean|*))|*}
 */
export function ruleValidDate(text) {
  let germanDateFormat = new RegExp("^\\d{2}.\\d{2}.\\d{4}$");

  return (v) => {
    // if the date is null, an empty string or undefined
    // there is no need to check the date, because there is nothing in it
    // e.g. the dateField is empty
    if (v === null || v === "" || v === undefined) {
      return true;
    }

    const parsedDate = parse(v, "dd.MM.yyyy", new Date());

    if (germanDateFormat.test(v)) {
      if (isValid(parsedDate)) {
        return true;
      } else {
        return text;
      }
    } else {
      return text;
    }
  }
}

/**
 * checks if the entered date is a valid date and past the current date
 *
 * @param text text if date is not valid
 * @returns {(function(*=): (boolean|*))|*}
 */
export function rulePastDate(text) {

  return (v) => {
    const parsedDate = parse(v, "dd.MM.yyyy", new Date());

    const _isBefore = isBefore(startOfDay(parsedDate), startOfDay(new Date()));
    const _isValid = isValid(parsedDate);

    if (_isValid && _isBefore) {
      return true;
    }
    return text;
  }
}

/**
 * Check if date is greater than maxDate
 *
 * @param text
 * @param maxDate
 * @returns {(function(*): (undefined|*|boolean))|*}
 */
export function ruleMaxDate(text, maxDate) {
  return (v) => {
    if (!v || !maxDate) return true;

    let currentDate = DateUtils.parseStringToDate(v);
    let maxDateRule = new Date(maxDate);

    // Set hours midnight to avoid the incorrect converting
    currentDate.setHours(0,0,0,0);
    maxDateRule.setHours(0,0,0,0);

    if (currentDate > maxDateRule) {
      return text;
    }

    return true;
  }
}

/**
 * Check if date less than minDate
 *
 * @param text
 * @param minDate
 * @returns {(function(*): (undefined|*|boolean))|*}
 */
export function ruleMinDate(text, minDate) {
  return (v) => {
    if (!v || !minDate) return true;

    let currentDate = DateUtils.parseStringToDate(v);
    let minDateRule = new Date(minDate);

    currentDate.setHours(0,0,0,0);
    minDateRule.setHours(0,0,0,0);

    if (currentDate < minDateRule) {
      return text;
    }

    return true;
  }
}

/**
 * checks if the entered date is a valid date and same or before the current date
 *
 * @param text text if date is not valid
 * @returns {(function(*=): (boolean|*))|*}
 */
export function ruleFutureDate(text) {
  return (v) => {
    const parsedDate = parse(v, "dd.MM.yyyy", new Date());

    const _isFuture = isAfter(startOfDay(parsedDate), startOfDay(new Date()));
    const _isEqual = isEqual(startOfDay(parsedDate), startOfDay(new Date()));
    const _isValid = isValid(parsedDate);

    if (_isValid && (_isFuture || _isEqual)) {
      return true;
    } else {
      return text;
    }
  }
}

/**
 * Checks if the value is equal to a specific variable.
 *
 * @param varToCheck variable to check equality
 * @param text to display on failure
 * @returns {function(*)}
 */
export function ruleIsEqual(varToCheck, text) {
  return (v) => (v === varToCheck) || text;
}
